
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { updateSEO } from '../../utils/seo';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Demo credentials for testing
  const validCredentials = {
    email: 'member@aiexchange.com',
    password: 'portal2024'
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Simulate authentication delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (email === validCredentials.email && password === validCredentials.password) {
      // Store authentication state
      localStorage.setItem('aiexchange_authenticated', 'true');
      localStorage.setItem('aiexchange_user_email', email);
      navigate('/portal');
    } else {
      setError('Invalid email or password. Please try again.');
    }

    setIsLoading(false);
  };

  useState(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
    
    const seoData = {
      title: 'Sign In - AI Exchange Member Portal Access',
      description: 'Sign in to access the exclusive AI Exchange member portal with verified agency listings and acquisition opportunities.',
      keywords: 'AI Exchange sign in, member portal login, exclusive access, verified listings',
      ogTitle: 'Sign In - AI Exchange Member Portal Access',
      ogDescription: 'Sign in to access the exclusive AI Exchange member portal with verified agency listings and acquisition opportunities.',
      ogImage: 'https://public.readdy.ai/ai/img_res/27ce28c2eac90bd1fcafc36b46bb6ee6.jpg',
      twitterCard: 'summary_large_image',
      canonicalUrl: `${siteUrl}/signin`
    };

    updateSEO(seoData);
  });

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="flex items-center justify-center min-h-[calc(100vh-160px)] px-6">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-light mb-4">
              Member <span className="font-semibold">Sign In</span>
            </h1>
            <p className="text-gray-400">
              Access your exclusive portal to verified AI business listings
            </p>
          </div>

          {/* Sign In Form */}
          <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your email"
                  required
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your password"
                  required
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Signing In...
                  </>
                ) : (
                  <>
                    <i className="ri-login-circle-line mr-2"></i>
                    Sign In to Portal
                  </>
                )}
              </Button>
            </form>

            {/* Additional Links */}
            <div className="mt-6 pt-6 border-t border-gray-800">
              <div className="text-center space-y-3">
                <button className="text-gray-400 hover:text-white transition-colors duration-200 text-sm cursor-pointer">
                  Forgot your password?
                </button>
                <div className="text-gray-500 text-sm">
                  Need access? <button 
                    onClick={() => navigate('/exchange')}
                    className="text-blue-400 hover:text-blue-300 transition-colors duration-200 cursor-pointer"
                  >
                    Apply for membership
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center text-gray-500 text-sm">
              <i className="ri-shield-check-line mr-2 text-green-400"></i>
              Your connection is secure and encrypted
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}